import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Check, MapPin, ShieldAlert } from "lucide-react";
import {
  customCitySelection,
  datasetCitySelection,
  searchMunicipalities
} from "./municipalityRepository";
import type { CitySelection, CitySource, SlovakMunicipality } from "./types";

type CityAutocompleteProps = {
  value: string;
  citySource?: CitySource;
  disabled?: boolean;
  required?: boolean;
  onChange: (selection: CitySelection) => void;
};

export function CityAutocomplete({ value, citySource, disabled = false, required = false, onChange }: CityAutocompleteProps) {
  const inputId = useId();
  const listId = `${inputId}-options`;
  const inputRef = useRef<HTMLInputElement>(null);
  const comboboxRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [displayValue, setDisplayValue] = useState(value);

  useEffect(() => {
    if (!isFocused) setDisplayValue(value);
  }, [isFocused, value]);

  const suggestions = useMemo(() => searchMunicipalities(displayValue), [displayValue]);
  const isOpen = isFocused && !disabled && displayValue.trim().length > 0 && suggestions.length > 0;

  const selectMunicipality = (municipality: SlovakMunicipality) => {
    const selection = datasetCitySelection(municipality);
    setDisplayValue(selection.city);
    onChange(selection);
    setIsFocused(false);
    inputRef.current?.blur();
  };

  return (
    <label className="market-field market-city-field" htmlFor={inputId}>
      <span>Mesto</span>
      <div className="market-city-combobox" ref={comboboxRef}>
        <MapPin className="market-city-icon" size={16} aria-hidden="true" />
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          value={displayValue}
          disabled={disabled}
          required={required}
          autoComplete="off"
          spellCheck={false}
          role="combobox"
          aria-autocomplete="list"
          aria-controls={listId}
          aria-expanded={isOpen}
          onFocus={() => setIsFocused(true)}
          onBlur={(event) => {
            if (!comboboxRef.current?.contains(event.relatedTarget)) setIsFocused(false);
          }}
          onChange={(event) => {
            const nextValue = event.target.value;
            setDisplayValue(nextValue);
            onChange(customCitySelection(nextValue));
          }}
        />

        {isOpen ? (
          <div className="market-city-options" id={listId} role="listbox" aria-label="Návrhy miest a obcí">
            {suggestions.map((municipality) => (
              <button
                key={`${municipality.name}-${municipality.district}`}
                type="button"
                role="option"
                aria-selected={citySource === "dataset" && displayValue === municipality.name}
                onPointerDown={(event) => event.preventDefault()}
                onClick={() => selectMunicipality(municipality)}
              >
                <span><strong>{municipality.name}</strong><small>okres {municipality.district}</small></span>
                <Check size={15} aria-hidden="true" />
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {displayValue.trim() && citySource === "custom" ? (
        <small className="market-city-status custom"><ShieldAlert size={13} aria-hidden="true" /> Vlastná hodnota · neoverené</small>
      ) : null}
      {displayValue.trim() && citySource === "dataset" ? (
        <small className="market-city-status"><Check size={13} aria-hidden="true" /> Štandardizovaný názov</small>
      ) : null}
    </label>
  );
}
